import { modalViewport } from '../utils/modalViewport'
import { useOverlayScrollLock } from '../hooks/useOverlayScrollLock'
/**
 * Arquivo: src/components/Modal.tsx
 * Versão: 5.6.5
 * Data: 07/10/2026
 */
import { X } from 'lucide-react'
import { useEffect, useId, useRef, type PropsWithChildren } from 'react'
import { createPortal } from 'react-dom'
import { protegerAposFechamentoPorToque } from '../utils/touchInteractionGuard'

interface ModalProps extends PropsWithChildren {
  aberto: boolean
  titulo: string
  subtitulo?: string
  largo?: boolean
  onClose: () => void
}

export default function Modal({ aberto, titulo, subtitulo, largo, onClose, children }: ModalProps) {
  useOverlayScrollLock(aberto)
  const tituloId = useId()
  const botaoFecharRef = useRef<HTMLButtonElement>(null)
  const onCloseRef = useRef(onClose)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!aberto) return

    const fecharComEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current()
    }
    window.addEventListener('keydown', fecharComEscape)

    // Em iOS/PWA, o teclado reduz a visual viewport sem necessariamente
    // reduzir 100dvh. Mantemos o modal sincronizado com a área realmente
    // visível para que rodapé e campos nunca fiquem atrás do teclado.
    const visualViewport = window.visualViewport
    const atualizarViewportModal = () => {
      const focused = document.activeElement
      const editing = focused instanceof HTMLElement && (
        focused.isContentEditable || focused instanceof HTMLTextAreaElement ||
        (focused instanceof HTMLInputElement && !['button', 'submit', 'reset', 'checkbox', 'radio', 'range', 'file', 'hidden'].includes(focused.type))
      )
      const viewport = modalViewport({
        layoutHeight: Math.max(window.innerHeight, document.documentElement.clientHeight),
        visualHeight: visualViewport?.height,
        offsetTop: visualViewport?.offsetTop,
        scale: visualViewport?.scale,
        editing,
      })
      document.documentElement.style.setProperty('--modal-visual-height', `${viewport.height}px`)
      document.documentElement.style.setProperty('--modal-visual-top', `${viewport.top}px`)
    }
    atualizarViewportModal()
    visualViewport?.addEventListener('resize', atualizarViewportModal)
    visualViewport?.addEventListener('scroll', atualizarViewportModal)
    window.addEventListener('orientationchange', atualizarViewportModal)
    window.addEventListener('resize', atualizarViewportModal)
    document.addEventListener('focusin', atualizarViewportModal)
    document.addEventListener('focusout', atualizarViewportModal)

    const foco = window.setTimeout(() => botaoFecharRef.current?.focus({ preventScroll: true }), 0)

    return () => {
      window.clearTimeout(foco)
      window.removeEventListener('keydown', fecharComEscape)
      visualViewport?.removeEventListener('resize', atualizarViewportModal)
      visualViewport?.removeEventListener('scroll', atualizarViewportModal)
      window.removeEventListener('orientationchange', atualizarViewportModal)
      window.removeEventListener('resize', atualizarViewportModal)
      document.removeEventListener('focusin', atualizarViewportModal)
      document.removeEventListener('focusout', atualizarViewportModal)
      document.documentElement.style.removeProperty('--modal-visual-height')
      document.documentElement.style.removeProperty('--modal-visual-top')
    }
  }, [aberto])

  if (!aberto) return null

  return createPortal(
    <div
      className="modal-backdrop"
      onClick={(event) => {
        if (event.target !== event.currentTarget) return
        event.preventDefault()
        event.stopPropagation()
        protegerAposFechamentoPorToque(event.detail)
        onCloseRef.current()
      }}
    >
      <div className={`modal-card${largo ? ' modal-wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={tituloId} onClick={(event) => event.stopPropagation()}>
        <div className="modal-mobile-handle" aria-hidden="true"><i /></div>
        <div className="modal-header">
          <div>
            <h2 id={tituloId}>{titulo}</h2>
            {subtitulo && <p className={subtitulo.includes('R' + '$') ? 'privacy-value' : undefined}>{subtitulo}</p>}
          </div>
          <button
            ref={botaoFecharRef}
            type="button"
            className="icon-button modal-close-button"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              protegerAposFechamentoPorToque(event.detail)
              onCloseRef.current()
            }}
            title="Fechar"
            aria-label="Fechar"
          ><X size={18} /></button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>,
    document.body,
  )
}

